import { Button, NonIdealState } from '@blueprintjs/core';
import * as React from 'react';
import {
  Redirect,
  Route,
  RouteComponentProps,
  RouteProps,
} from 'react-router-dom';

import { Loading } from 'src/ui/components/Loading';
import { useProfile } from 'src/ui/hooks/connection';
import { useListOrganizations } from 'src/ui/hooks/organization';
import { copy } from 'src/ui/templates/copy';

interface OwnProps extends RouteProps {
  children: React.ReactElement;
  path: string | string[];
  adminOnly?: boolean;
  staffOnly?: boolean;
  voiceboxOnly?: boolean;
  voiceboxAPIAccessEnabled?: boolean;
}

/**
 * A wrapper for <Route> that redirects to the login Route if not authenticated.
 */
export const PrivateRoute: React.VFC<OwnProps> = ({
  children,
  path,
  adminOnly = false,
  staffOnly = false,
  voiceboxOnly = false,
  voiceboxAPIAccessEnabled = false,
  ...rest
}) => {
  const {
    isLoading: isProfileLoading,
    isError: isProfileError,
    data: profileData,
    refetch: refetchProfile,
    remove: removeProfile,
  } = useProfile();
  const isConnected = profileData && profileData.profile?.is_authenticated;
  const isSuperUser = profileData && profileData.profile?.is_superuser;
  const isStaff = profileData && profileData.profile?.is_staff;
  const isVoiceboxEnabledUser =
    profileData && profileData.profile?.is_voicebox_enabled;
  const isVoiceboxAPIAccessEnabled =
    profileData && profileData.profile?.is_voicebox_api_access_enabled;

  const {
    isLoading: isOrgsLoading,
    isError: isOrgsError,
    data: orgsData,
    refetch: refetchOrgs,
    remove: removeOrgs,
  } = useListOrganizations();
  const hasOrg = (domain: string) =>
    orgsData?.listOrganizations?.some((org) => org?.domain === domain);

  const reload = () => {
    removeProfile();
    refetchProfile({ cancelRefetch: true });
    removeOrgs();
    refetchOrgs({ cancelRefetch: true });
  };

  const action = <Button onClick={() => reload()}>{copy.login.retry}</Button>;

  const errorState = (
    <div className="sd-container">
      <NonIdealState
        icon="error"
        title="Error"
        description={copy.errors.connectionFailed}
        action={action}
      />
    </div>
  );

  const renderFunc = (routeProps: RouteComponentProps<{ org?: string }>) => {
    const { org = '' } = routeProps.match.params;

    const next = routeProps.location?.pathname || '/';
    const search = routeProps.location?.search || '';
    const redirectTo = {
      pathname: '/login',
      search: `?next=${next}${search}`,
    };

    if (isProfileLoading) {
      return <Loading />;
    }

    if (isProfileError) {
      return errorState;
    }

    // Redirect to login before any org-membership work, otherwise an org route
    // hangs on the listOrganizations query when the session is expired.
    if (!isConnected) {
      return <Redirect to={redirectTo} />;
    }

    if (org && isOrgsLoading) {
      return <Loading />;
    }

    if (org && isOrgsError) {
      return errorState;
    }

    if (adminOnly && !isSuperUser) {
      // If this is admin only we don't need to do handle un-authenticated
      // use case because this should only be admins anyway they should login first.
      // Otherwise this would end up in a login redirect loop if a non-admin visits.
      return <Redirect to="/" />;
    }

    if (staffOnly && !isStaff) {
      // If this is admin only we don't need to do handle un-authenticated
      // use case because this should only be admins anyway they should login first.
      // Otherwise this would end up in a login redirect loop if a non-admin visits.
      return <Redirect to="/" />;
    }

    if (org && !hasOrg(org)) {
      // Do not show the org routes for users who do not belong to the org
      return <Redirect to="/" />;
    }

    if (voiceboxOnly && !isVoiceboxEnabledUser) {
      // Do not show the voicebox route for non-voicebox enabled users
      return <Redirect to="/" />;
    }

    if (voiceboxAPIAccessEnabled && !isVoiceboxAPIAccessEnabled) {
      // Do not show the voicebox route users who do not have access to voicebox APIs
      return <Redirect to="/" />;
    }

    return children;
  };

  return <Route {...rest} path={path} render={renderFunc} />;
};
